import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { InputBase, Slider } from '@mui/material';

import FilterSection from '@/components/Search/components/Filters/common/FilterSection/FilterSection';

import messages from './messages';

import useStyles from './styles';

type Props = {
  onSliderValueChange: Function;
  sliderValue: number[];
};

export const MAX_VALUE = 60;
const MAX_VALUE_FLOOR_THRESHOLD = 20;
const MIN_VALUE = 15;
const formatPricesValue = (value: number) => {
  if (value === MAX_VALUE) {
    return `${value} € et +`;
  }
  return `${value} €`;
};

const PriceFilter: FC<Props> = ({ onSliderValueChange, sliderValue }) => {
  const { classes } = useStyles();
  const { formatMessage } = useIntl();

  const [minPrice, setMinPrice] = useState(formatPricesValue(sliderValue[0]));
  const [maxPrice, setMaxPrice] = useState(formatPricesValue(sliderValue[1]));

  const onSliderChange = (_: Event, newValue: number[]) => {
    const [minValueClamped, maxValueClamped] = [
      newValue[0],
      Math.max(MAX_VALUE_FLOOR_THRESHOLD, newValue[1]),
    ];
    onSliderValueChange([minValueClamped, maxValueClamped]);
    setMinPrice(formatPricesValue(minValueClamped));
    setMaxPrice(formatPricesValue(maxValueClamped));
  };

  const getInputValue = (value: number | null, id: string) => {
    const [sliderMin, sliderMax] = sliderValue;
    if (!value) {
      return [MIN_VALUE, sliderMax];
    }
    if (value >= MAX_VALUE) {
      return [sliderMin, MAX_VALUE];
    }

    if (id === 'min') {
      const minValueClamped = Math.max(MIN_VALUE, value);
      return minValueClamped > sliderMax
        ? [sliderMax, minValueClamped]
        : [minValueClamped, sliderMax];
    }

    const maxValueClamped = Math.max(MAX_VALUE_FLOOR_THRESHOLD, value);
    return maxValueClamped < sliderMin
      ? [maxValueClamped, sliderMin]
      : [sliderMin, maxValueClamped];
  };

  const onInputPriceBlur = (e) => {
    const { value, id } = e.target;
    const parsedValue = getInputValue(parseInt(value.split('€')[0], 10), id);
    onSliderChange(null, parsedValue);
  };

  return (
    <FilterSection title={formatMessage(messages.title)}>
      <Slider
        classes={{
          root: classes.slider,
          rail: classes.rail,
          track: classes.track,
          thumb: classes.thumb,
        }}
        value={sliderValue}
        onChange={onSliderChange}
        aria-labelledby="price-slider"
        min={MIN_VALUE}
        max={MAX_VALUE}
      />
      <div className={classes.inputPricesContainer}>
        <InputBase
          id="min"
          classes={{
            root: classes.textField,
            input: classes.textInput,
          }}
          value={minPrice}
          onChange={(e) => setMinPrice(e.target.value)}
          onBlur={onInputPriceBlur}
          inputProps={{
            'aria-labelledby': 'prix minimum',
          }}
        />
        <InputBase
          classes={{
            root: classes.textField,
            input: classes.textInput,
          }}
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
          onBlur={onInputPriceBlur}
          inputProps={{
            'aria-labelledby': 'prix maximum',
          }}
        />
      </div>
    </FilterSection>
  );
};

export default PriceFilter;
